import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  SBAComboBox,
} from "../../UiComponents/SBADropDown";
import { SBDatePicker } from "../../UiComponents/SBDatePicker";
import { sbaAxios } from "../../utils/sbaAxios";
import { AgGridReact } from "ag-grid-react";
import {
  getPortfolioListAPI,
  getLiquidityCashFlowAssets,
  getLiquidityCashFlow,
  deleteLiquidityCashFlow,
  insertOrUpdateCashFlow,
  getCashFlowModel,
} from "../../../config/apiConfig";
import moment from "moment";
import { Modal } from "react-bootstrap";
import {
  Chart,
  ChartLegend,
  ChartTitle,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartSeries,
  ChartSeriesItem,
} from "@progress/kendo-react-charts";
import CurrencyInput from "react-currency-input-field";

export const CashFlowTab = () => {
  const [portfolioList, setPortfolioList] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [assetList, setAssetList] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [cashFlowDate, setCashFlowDate] = useState(new Date());
  const [cashFlowData, setCashFlowData] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const [showModel, setShowModel] = useState(false);
  const [gridAPI, setGridAPI] = useState(null);
  const [modifiedRow, setModifiedRow] = useState([]);
  const [modelData, setModelData] = useState(null);
  const [enableControls, setEnableControls] = useState(false);
  const [selectedTFrequency, setSelectedTFrequency] = useState(null);
  const prevTFrequency = useRef(selectedTFrequency);

  const TFrequency = [
    {tFrequencyName: "Monthly", tFrequencyId: 3},
    {tFrequencyName: "Quarterly", tFrequencyId: 4},
    {tFrequencyName: "Semi-Annually", tFrequencyId: 6},
    {tFrequencyName: "Annually", tFrequencyId: 7}
  ];

  const onGridReady = (params) => {
    setGridAPI(params.api);
  };
  const gridRef = useRef(null);

  useEffect(()=>{
    if(selectedPortfolio && selectedAsset && cashFlowData.length>0){
      setEnableControls(true);
    }
    else{
      setEnableControls(false);
    }
  },[selectedPortfolio, selectedAsset, cashFlowData]);

  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const list = await sbaAxios({
          url: getPortfolioListAPI,
        });
        setPortfolioList(list);
      } catch (e) {
        setPortfolioList([]);
      }
    };
    fetchPortfolios();
  }, []);

  useEffect(() => {
    const fetchAssetsByPortfolio = async () => {
      try {
        const list = await sbaAxios({
          url: getLiquidityCashFlowAssets + `/${selectedPortfolio.PortfolioId}`,
        });
        setAssetList(list);
      } catch (e) {
        setAssetList([]);
      }
    };
    if (selectedPortfolio) {
      fetchAssetsByPortfolio();
      setSelectedAsset(null);
    }
  }, [selectedPortfolio]);

  const fetchCashFlowData = async () => {
    if(selectedPortfolio === null && selectedAsset === null){
      toast.error('Please select Portfolio and Asset')
      return
    }
    try {
      const formattedDate = moment(cashFlowDate).format("MM-DD-YYYY");
      const list = await sbaAxios({
        url:
          getLiquidityCashFlow +
          `/${selectedPortfolio.PortfolioId},${selectedAsset.AssetId},${formattedDate}`,
      });
      const modifiedList = list.map((item, index) => ({
        ...item,
        rowId: index + 1,
      }));
      setCashFlowData(modifiedList);
      setSelectedTFrequency(()=>{
        const tfreq = list[0]?.TransactionFrequencyId || list[1]?.TransactionFrequencyId;
        return TFrequency.find((item) => item.tFrequencyId === tfreq) || null;
      });
    } catch (e) {
      setCashFlowData([]);
      setSelectedTFrequency(null);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await sbaAxios({
        url:
          deleteLiquidityCashFlow + `/${selectedRow.PortfolioAssetCashFlowId}`,
      });
      toast.success("Selected Cash flow deleted successfully.");
    } catch (e) {
      toast.error("Unable to delete Cash flow.");
    } finally {
      fetchCashFlowData();
      setShowModel(false);
    }
  };

  const actionCellRenderer = (params) => {
    return (
      <div
        className="actions-cell"
        style={{
          display: params.data.PortfolioAssetCashFlowId === 0 ? "none" : "",
        }}
      >
        <i
          className="fa fa-trash"
          style={{
            color: params.data.IsLocked ? "gray" : "red",
            margin: "0 8px",
            cursor: params.data.IsLocked ? "not-allowed" : "pointer",
          }}
          onClick={() => {
            if (!params.data.IsLocked) {
              handleDeleteClick(params);
            }
          }}
        ></i>
      </div>
    );
  };

  const columnDefs = [
    {
      headerName: "Date",
      field: "CashFlowDate",
      valueFormatter: (params) => {
        if (!params.value) return ""; // Handle cases where the date is null or undefined
        return moment(params.value).format("MM/DD/YYYY");
      },
    },
    {
      headerName: "Distribution",
      field: "DistributionAmount",
      valueFormatter: (params) => {
        if (params.value == null) return "$0.00"; // Handle null or undefined values
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(params.value);
      },
      editable: true,
      cellStyle: {
        textAlign: "right", // Align text to the right
      },
    },
    {
      headerName: "Contribution",
      field: "ContributionAmount",
      valueFormatter: (params) => {
        if (params.value == null) return "$0.00"; // Handle null or undefined values
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(params.value);
      },
      editable: true,
      cellStyle: {
        textAlign: "right", // Align text to the right
      },
    },
    { headerName: "Action", field: "Action", cellRenderer: actionCellRenderer },
  ];

  const handleSelectedPortfolio = (e) => {
    setSelectedPortfolio(e.value);
  };

  const handleSelectedAsset = (e) => {
    setSelectedAsset(e.value);
  };

  const handleDeleteClick = (params) => {
    setSelectedRow(params.data);
    setShowModel(true);
  };

  const handleAddOrUpdate = async (tFrequencySave = false, id) => {
    if (modifiedRow.length !== 0 || tFrequencySave) {
      try {
          await sbaAxios({
            url: insertOrUpdateCashFlow,
            data: (modifiedRow.length !== 0 ? modifiedRow : cashFlowData).map((row) => ({
              portfolioAssetCashFlowId: row.PortfolioAssetCashFlowId,
              portfolioId: row.PortfolioId,
              assetId: row.AssetId,
              cashFlowDate: row.CashFlowDate,
              cashFlowAmount: row.CashFlowAmount,
              distributionAmount: row.DistributionAmount,
              contributionAmount: row.ContributionAmount,
              isLocked: row.IsLocked,
              transactionFrequencyId: tFrequencySave ? id: selectedTFrequency?.tFrequencyId
            })),
            method: "post",
          });
          fetchCashFlowData();
          setModifiedRow([]);
          toast.success("Cash flows saved successfully.");
        }
       catch (e) {
        toast.error("Unable to save cash flow.");
      }
    } else {
      toast.error("Nothing has been added or modified.");
    }
  };

  const handleModelData = (data) => {
    setModelData(data);
  };

  const handleLoadModel = () => {
      gridRef?.current.api.forEachNode((node) => {
        const model = modelData?.AssetModel?.find((item) => item.ModelingDate === node.data.CashFlowDate);
        if(model){
          node.setDataValue("DistributionAmount", model.DistAmount);
          node.setDataValue("ContributionAmount", model.CapitalCallAmount);
          modifiedRow.push(node.data);
        }
      });
      setSelectedTFrequency(modelData?.tFrequency);
  };

  const getCashFlowDate = () => {
    const lastDate = cashFlowData[cashFlowData.length - 1].CashFlowDate;
    const date = new Date(lastDate);
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString();
  };

  const addNewRow = () => {
    if (cashFlowData.length > 0) {
      const newRow = {
        rowId: cashFlowData[cashFlowData.length - 1].rowId + 1,
        PortfolioAssetCashFlowId: 0,
        PortfolioId: selectedPortfolio?.PortfolioId,
        PortfolioName: selectedPortfolio?.PortfolioName,
        AssetId: selectedAsset?.AssetId,
        AssetName: selectedAsset?.AssetName,
        CashFlowDate: getCashFlowDate(),
        CashFlowAmount: 0.0,
        distributionAmount: 0.0,
        contributionAmount: 0.0,
        IsLocked: false,
      };
      console.log("newRow", newRow);
      gridAPI.applyTransaction({ add: [newRow] });
    } else {
      toast.error("No Cash flow data found.");
    }
  };

  const onCellEditingStopped = (event) => {
    setModifiedRow((prevRows) => [...prevRows, event.data]);
  };

  return (
    <>
      <div className="container-fluid p-4">
        <div className="row">
          <div className="col-4 h-75">
            <div className="row">
              <div className="col-md-10">
                <label className="label">Portfolio</label>
                <div>
                  <SBAComboBox
                    className="w-100"
                    data={portfolioList}
                    textField={"PortfolioName"}
                    valueField={"PortfolioId"}
                    value={selectedPortfolio}
                    onChange={handleSelectedPortfolio}
                    popupSettings={{ className: "modalDD" }}
                  ></SBAComboBox>
                </div>
              </div>
            </div>
            <div className="row my-4">
              <div className="col-md-10">
                <label className="label">Asset</label>
                <div>
                  <SBAComboBox
                    className="w-100"
                    data={assetList}
                    textField={"AssetName"}
                    valueField={"AssetId"}
                    value={selectedAsset}
                    onChange={handleSelectedAsset}
                    popupSettings={{ className: "modalDD" }}
                  ></SBAComboBox>
                </div>
              </div>
            </div>

            <div className="row my-4" style={{ width: "135%" }}>
              <div className="col-md-6" style={{ width: "66%" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {" "}
                  {/* Changed alignment to "center" */}
                  <div style={{ flex: 1 }}>
                    <label className="label">Cash Flow Date</label>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {" "}
                      {/* Added flexbox for input and button */}
                      <SBDatePicker
                        className="w-75"
                        value={moment(cashFlowDate)}
                        onChange={(newDate) => setCashFlowDate(newDate)}
                      />
                      <button
                        type="submit"
                        className="p-sm-btn"
                        onClick={() => {
                          fetchCashFlowData();
                        }}
                      >
                        Go
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row my-4" style={{ width: "100%" }}>
              <div className="col">
                <div
                  style={{ display: "flex", justifyContent: "end", gap: "5px" }}
                >
                  <button
                    type="submit"
                    className="p-sm-btn"
                    onClick={handleLoadModel}
                    disabled={!modelData}
                  >
                    Load Model
                  </button>
                  <button
                    type="submit"
                    className="p-sm-btn"
                    onClick={() => {
                      addNewRow();
                    }}
                  >
                    Add Row
                  </button>
                  <button
                    type="submit"
                    className="p-sm-btn"
                    onClick={() => {
                      handleAddOrUpdate();
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-6">
                <label className="label">Transaction Frequency</label>
                <div className="d-flex align-items-center">
                  <SBAComboBox
                    className="w-100"
                    data={TFrequency}
                    textField={"tFrequencyName"}
                    valueField={"tFrequencyId"}
                    value={selectedTFrequency}
                    onChange={(e) => {
                      setSelectedTFrequency(e.value);
                      if (prevTFrequency.current !== e.value) {
                        prevTFrequency.current = e.value;
                        handleAddOrUpdate(true, e.value.tFrequencyId); // Pass a flag to indicate a forced save
                      }
                    }}
                    disabled={!enableControls}
                    popupSettings={{ className: "modalDD" }}
                  />
                </div>
              </div>
            </div>

            <div
              className="row my-4"
              style={{ height: "500px", width: "auto" }}
            >
              <div className="row col-12 h-100">
                <div
                  className="ag-theme-alpine"
                  style={{ height: "100%", width: "100%", paddingTop: 0 }}
                >
                  <AgGridReact
                    ref={gridRef}
                    rowData={cashFlowData}
                    columnDefs={columnDefs}
                    onGridReady={onGridReady}
                    onCellEditingStopped={onCellEditingStopped}
                    defaultColDef={{ flex: 1, resizable: true, minWidth: 100 }}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* <div className="col-8">
            <AssetModelling />
          </div> */}

          <div className="col-8">
            <div className="d-flex">
              <div
                className="border-end"
                style={{
                  width: "1px",
                  height: "800px",
                  backgroundColor: "#dee2e6",
                }}
              ></div>
              <AssetModelling
                handleModelData={handleModelData}
                enableControls={enableControls}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal show={showModel} onHide={() => setShowModel(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Cash flow</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            The selected cash flow will be deleted permanently. Are you sure?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button className="s-sm-btn mx-1" onClick={() => setShowModel(false)}>
            Cancel
          </button>
          <button className="p-sm-btn mx-1" onClick={handleDeleteConfirm}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const AssetModelling = ({handleModelData, enableControls}) => {
  const [startDate, setStartDate] = useState(new Date());
  const [lifeoffund, setLifeoffund] = useState(null);
  const [committment, setCommitment] = useState(null);
  const [initialnav, setInitialnav] = useState(null);
  const [year1Conrate, setYear1Conrate] = useState(null);
  const [year2Conrate, setYear2Conrate] = useState(null);
  const [subsYearConrate, setSubsYearConrate] = useState(null);
  const [growthRate, setGrowthRate] = useState(null);
  const [bow, setBow] = useState(null);
  const [yeild, setYeild] = useState(null);
  const [graphData, setGraphData] = useState({});
  const [selectedTFrequency, setSelectedTFrequency] = useState(null);

  const TFrequency = [
    {tFrequencyName: "Monthly", tFrequencyId: 3},
    {tFrequencyName: "Quarterly", tFrequencyId: 4},
    {tFrequencyName: "Semi-Annually", tFrequencyId: 6},
    {tFrequencyName: "Annually", tFrequencyId: 7}
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "lifeoffund") {
      if (/^\d*$/.test(value)) {
        setLifeoffund(value);
      }
    } else if (name === "y1contrate") {
      setYear1Conrate(value);
    } else if (name === "y2contrate") {
      setYear2Conrate(value);
    } else if (name === "subsyearcontrate") {
      setSubsYearConrate(value);
    } else if (name === "growthrate") {
      setGrowthRate(value);
    } else if (name === "bow") {
      setBow(value);
    } else if (name === "yeild") {
      setYeild(value);
    }
  };

  const requiredFields = {
    lifeoffund,
    committment,
    initialnav,
    year1Conrate,
    year2Conrate,
    subsYearConrate,
    growthRate,
    bow,
    yeild
  };

  const fetchGraphData = async () => {
    if (committment === "0" || committment === 0 || committment === 0.0) {
      toast.error("Commitment can't be zero");
      return;
    }
    if (lifeoffund === "0" || lifeoffund === 0 || lifeoffund === 0.0) {
      toast.error("Life Of Fund can't be zero");
      return;
    }
    for (const [field, value] of Object.entries(requiredFields)) {
      if (value === null || value === "") {
        if (field === "committment" && (value === "0" || value === 0)) {
          toast.error("Commitment can't be zero");
          return;
        }
        toast.error("All fields are required.");
        return;
      }
    }
    if(!selectedTFrequency){
      toast.error("All fields are required.");
      return;
    }
    try {
      const formattedStartDate = startDate.toISOString().split("T")[0];
      const response = await sbaAxios({
        url:
          getCashFlowModel +
          `/${formattedStartDate},${lifeoffund},${committment},${initialnav},${year1Conrate},${year2Conrate},${subsYearConrate},${growthRate},${bow},${yeild}`,
      });
      setGraphData(response);
      handleModelData({...response, tFrequency:selectedTFrequency});
    } catch (e) {
      handleModelData({});
      setGraphData({});
    }
  };

  return (
    <div className="container-fluid" style={{ paddingTop: 0 }}>
      <div className="h4 mb-4">Asset Modeling</div>
      <div className="row" style={{ height: "75px" }}>
        <div className="col-3">
          <label className="label">Start Date</label>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            {" "}
            {/* Added flexbox for input and button */}
            <SBDatePicker
              className="w-75"
              value={moment(startDate)}
              onChange={(newDate) => setStartDate(newDate)}
              disabled={!enableControls}
            />
          </div>
        </div>
        <div className="col-3">
          <label className="label">
            Life Of Fund (Yrs)<span className="mandatory">*</span>
          </label>
          <input
            type="number"
            className="w-75 input-default form-control text-right no-arrows"
            name="lifeoffund"
            value={lifeoffund}
            onChange={handleInputChange}
            onKeyDown={(event) => {
              if (!/[0-9]/.test(event.key)) {
                if (
                  event.key !== "Backspace" &&
                  event.key !== "Delete" &&
                  event.key !== "Tab"
                ) {
                  // Allow Backspace and Delete
                  event.preventDefault(); // Prevent all other keys
                }
              }
            }}
            step="0"
            onFocus={(event) => event.target.select()}
            disabled={!enableControls}
          />
        </div>
        <div className="col-3">
          <label className="label">
            Commitment<span className="mandatory">*</span>
          </label>
          <CurrencyInput
            name="commitment"
            className="w-75 input-default form-control text-right no-arrows"
            value={committment}
            decimalsLimit={2} // Limit to two decimal places
            onValueChange={(value) => setCommitment(value || "0")}
            prefix="$" // Add a dollar sign as a prefix
            disabled={!enableControls}
            onFocus={(event) => event.target.select()}
          />
        </div>
        <div className="col-3">
          <label className="label">
            Initial NAV<span className="mandatory">*</span>
          </label>
          <CurrencyInput
            name="initialnav"
            className="w-75 input-default form-control text-right no-arrows"
            value={initialnav}
            decimalsLimit={2} // Limit to two decimal places
            onValueChange={(value) => setInitialnav(value || "0")}
            prefix="$" // Add a dollar sign as a prefix
            disabled={!enableControls}
            onFocus={(event) => event.target.select()}
          />
        </div>
      </div>
      <div className="row" style={{ height: "75px" }}>
        <div className="col-3">
          <label className="label">
            Year 1 Cont. Rate<span className="mandatory">*</span>
          </label>
          <input
            type="number"
            className="w-75 input-default form-control text-right no-arrows"
            name="y1contrate"
            value={year1Conrate}
            onChange={handleInputChange}
            disabled={!enableControls}
            onFocus={(event) => event.target.select()}
          />
        </div>
        <div className="col-3">
          <label className="label">
            Year 2 Cont. Rate<span className="mandatory">*</span>
          </label>
          <input
            type="number"
            className="w-75 input-default form-control text-right no-arrows"
            name="y2contrate"
            value={year2Conrate}
            onChange={handleInputChange}
            disabled={!enableControls}
            onFocus={(event) => event.target.select()}
          />
        </div>
        <div className="col-3">
          <label className="label">
            Subsequent Year Cont. Rate<span className="mandatory">*</span>
          </label>
          <input
            type="number"
            className="w-75 input-default form-control text-right no-arrows"
            name="subsyearcontrate"
            value={subsYearConrate}
            onChange={handleInputChange}
            disabled={!enableControls}
            onFocus={(event) => event.target.select()}
          />
        </div>
        <div className="col-3">
          <label className="label">
            Growth Rate<span className="mandatory">*</span>
          </label>
          <input
            type="number"
            className="w-75 input-default form-control text-right no-arrows"
            name="growthrate"
            value={growthRate}
            onChange={handleInputChange}
            disabled={!enableControls}
            onFocus={(event) => event.target.select()}
          />
        </div>
      </div>
      <div className="row" style={{ height: "75px" }}>
        <div className="col-3">
          <label className="label">
            Bow<span className="mandatory">*</span>
          </label>
          <input
            type="number"
            className="w-75 input-default form-control text-right no-arrows"
            name="bow"
            value={bow}
            onChange={handleInputChange}
            disabled={!enableControls}
            onFocus={(event) => event.target.select()}
          />
        </div>
        <div className="col-3">
          <label className="label">
            Yield<span className="mandatory">*</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <input
              type="number"
              className="w-75 input-default form-control text-right no-arrows"
              name="yeild"
              value={yeild}
              onChange={handleInputChange}
              disabled={!enableControls}
              onFocus={(event) => event.target.select()}
            />
          </div>
        </div>
        <div className="col-3">
          <label className="label">Transaction Frequency</label>
          <div className="d-flex align-items-center">
            <SBAComboBox
              className="w-75"
              data={TFrequency}
              textField={"tFrequencyName"}
              valueField={"tFrequencyId"}
              value={selectedTFrequency}
              onChange={(e) => setSelectedTFrequency(e.value)}
              disabled={!enableControls}
              popupSettings={{ className: "modalDD" }}
            />
          </div>
        </div>
        <div className="col-3 align-content-center">
          <div className="d-flex justify-content-start">
          <button
              type="submit"
              className="p-sm-btn"
              onClick={fetchGraphData}
              disabled={!enableControls}
            >
              Go
            </button>
          </div>
        </div>
      </div>
      <div className="row" style={{ height: "400px" }}>
        <div className="col-4">
          <CommitmentGraph
            cTitle={"Net Asset Value"}
            dataKey="AssetModel"
            data={graphData}
            valueKey="NavPercentOfCommit"
            color="#FF5733"
          />
        </div>
        <div className="col-4">
          <CommitmentGraph
            cTitle={"Cumulative Contributions"}
            dataKey="CommulativeCommitments"
            data={graphData}
            valueKey="ModelingPercentOfCommit"
            color="#28B463"
          />
        </div>
        <div className="col-4">
          <CommitmentGraph
            cTitle={"Cumulative Distributions"}
            dataKey="CumulativeDistributions"
            data={graphData}
            valueKey="ModelingPercentOfCommit"
            color="#3498DB"
          />
        </div>
      </div>
      {/* <div className="row">
        <button
          type="submit"
          className="p-sm-btn"
          style={{ width: "50px", paddingLeft: "10px" }}
          onClick={() => console.log("using cashflow")}
        >
          Use Cash Flow
        </button>
      </div>
      <div className="row">
        <DistributionGrid gridData={graphData["AssetModel"]} />
      </div> */}
    </div>
  );
};

const CommitmentGraph = ({ cTitle, dataKey, data, valueKey, color }) => {
  return (
    <>
      <Chart>
        <ChartTitle text={cTitle} />

        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            categories={data[dataKey]?.map(
              (item) => item.YearNr
            )} // Extract and format dates
            title={{ text: "Year" }}
            baseUnit="years" // Group by year if needed
            labels={{ rotation:45 }} // Format date labels
          />
        </ChartCategoryAxis>

        <ChartSeries>
          <ChartSeriesItem
            type="line"
            data={data[dataKey]?.map((item) => item[valueKey] /100)} // Map dynamic valueKey
            color={color || "#05BFDB"}
            tooltip={{ visible: true, format: "p2" }} // Display values as currency
            name={cTitle}
          />
        </ChartSeries>

        <ChartLegend position="bottom" visible={true} />

        <ChartValueAxis>
          <ChartValueAxisItem
            title={{ text: "Percent Of Committment" }}
            labels={{ format: "p" }} // Format Y-axis labels as currency
            majorGridLines={{ visible: false }}
          />
        </ChartValueAxis>
      </Chart>
    </>
  );
};

const DistributionGrid = ({ gridData }) => {
  const actionCellRenderer = (params) => {
    return (
      <div className="actions-cell">
        <i
          className="fa fa-trash"
          style={{
            color: "red",
            margin: "0 8px",
            cursor: "pointer",
          }}
          onClick={() => {
            console.log(params.data);
          }}
        ></i>
      </div>
    );
  };
  const columnDefs = [
    {
      headerName: "Date",
      field: "ModelingDate",
      valueFormatter: (params) => {
        if (!params.value) return ""; // Handle cases where the date is null or undefined
        return moment(params.value).format("MM/DD/YYYY");
      },
    },
    {
      headerName: "Distribution",
      field: "DistAmount",
      valueFormatter: (params) => {
        return params.value.toFixed(2);
      },
    },
    {
      headerName: "Delete",
      field: "",
      cellRenderer: actionCellRenderer,
    },
  ];
  return (
    <div className="row my-4" style={{ height: "500px", width: "600px" }}>
      <div className="row col-12 h-100">
        <div
          className="ag-theme-alpine"
          style={{ height: "100%", width: "100%", paddingTop: 0 }}
        >
          <AgGridReact
            rowData={gridData}
            columnDefs={columnDefs}
            defaultColDef={{ flex: 1, resizable: true, minWidth: 200 }}
          />
        </div>
      </div>
    </div>
  );
};
